import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { API } from "../config/api";
import { format } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Archive,
  Scale,
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  CheckSquare,
  Bell,
  LogOut,
  Menu,
  X,
  User,
  Calendar,
  Globe,
  FileCheck,
  Mail,
  Settings,
  ClipboardList,
  CalendarClock,
  FileSpreadsheet,
  FolderKanban,
  ArrowUp,
  Timer,
  HandCoins,
} from "lucide-react";
import axios from "axios";


export default function DashboardLayout() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        axios.get(`${API}/notifications`, getAuthHeader()),
        axios.get(`${API}/notifications/unread-count`, getAuthHeader()),
      ]);
      setNotifications(notifRes.data.slice(0, 10));
      setUnreadCount(countRes.data.count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.post(`${API}/notifications/${notificationId}/read`, {}, getAuthHeader());
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, getAuthHeader());
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const adminNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/team", icon: Users, label: "Echipă" },
    { to: "/clients", icon: Briefcase, label: "Clienți" },
    { to: "/cases", icon: FileText, label: "Dosare" },
    { to: "/evidenta-dosare", icon: FileSpreadsheet, label: "Evidență Dosare" },
    { to: "/dosare-bc", icon: FolderKanban, label: "Dosare BC" },
    { to: "/evidenta-bc", icon: FileSpreadsheet, label: "Evidență BC" },
    { to: "/dosare-arhivate", icon: Archive, label: "Dosare Arhivate" },
    { to: "/tasks", icon: CheckSquare, label: "Sarcini" },
    { to: "/admin-notes", icon: ClipboardList, label: "Sarcini Personale" },
    { to: "/appointments", icon: CalendarClock, label: "Programări" },
    { to: "/calendar", icon: Calendar, label: "Calendar" },
    { to: "/billing", icon: Timer, label: "Time Tracking" },
    { to: "/retrocedari", icon: HandCoins, label: "Retrocedări" },
    { to: "/portal-just", icon: Globe, label: "Portal Just" },
    { to: "/document-requests", icon: FileCheck, label: "Cereri Documente" },
    { to: "/automated-emails", icon: Mail, label: "Email-uri Automate" },
    { to: "/settings", icon: Settings, label: "Setări" },
  ];

  const teamNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/cases", icon: FileText, label: "Dosarele Mele" },
    { to: "/tasks", icon: CheckSquare, label: "Sarcinile Mele" },
    { to: "/billing", icon: Timer, label: "Time Tracking" },
    { to: "/calendar", icon: Calendar, label: "Calendar" },
    { to: "/document-requests", icon: FileCheck, label: "Cereri Documente" },
  ];

  // Avocat Colaborator has access to Programări
  const avocatColaboratorNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/cases", icon: FileText, label: "Dosarele Mele" },
    { to: "/tasks", icon: CheckSquare, label: "Sarcinile Mele" },
    { to: "/billing", icon: Timer, label: "Time Tracking" },
    { to: "/appointments", icon: CalendarClock, label: "Programări" },
    { to: "/calendar", icon: Calendar, label: "Calendar" },
    { to: "/document-requests", icon: FileCheck, label: "Cereri Documente" },
  ];

  // Colaborator Extern has access to Dosare BC and Evidenta BC (view only)
  const colaboratorExternNavItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/dosare-bc", icon: FolderKanban, label: "Dosare BC" },
    { to: "/evidenta-bc", icon: FileSpreadsheet, label: "Evidență BC" },
  ];

  const getNavItems = () => {
    if (user?.role === "admin") return adminNavItems;
    if (user?.position === "colaborator_extern") return colaboratorExternNavItems;
    if (user?.position === "avocat_colaborator") return avocatColaboratorNavItems;
    return teamNavItems;
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - fixed on mobile, sticky on desktop */}
      <aside
        className={`fixed lg:sticky lg:top-0 inset-y-0 left-0 z-50 w-64 h-screen bg-card border-r transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                <Scale className="w-5 h-5 text-primary-foreground" />
              </div>
              <div className="flex flex-col">
                <span className="font-heading text-xl font-bold">Legal<span className="text-teal-600">Flow</span></span>
                <span className="text-xs text-muted-foreground">by Dan Codrean</span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg font-body text-sm transition-colors ${
                      isActive
                        ? "bg-teal-600 text-white"
                        : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          {/* User info */}
          <div className="p-4 border-t">
            <div className="flex items-center gap-3 px-2">
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-body text-sm font-medium truncate">{user?.name}</p>
                <p className="font-mono text-xs text-muted-foreground capitalize">
                  {user?.role === "admin" ? "Administrator" : "Echipă"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-card/80 backdrop-blur-md border-b">
          <div className="flex items-center justify-between px-4 lg:px-6 h-16">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
              data-testid="mobile-menu-button"
            >
              <Menu className="w-5 h-5" />
            </Button>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              {/* Notifications */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs bg-accent text-accent-foreground">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </Badge>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80" data-testid="notifications-dropdown">
                  <DropdownMenuLabel className="flex items-center justify-between">
                    <span className="font-heading">Notificări</span>
                    {unreadCount > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
                        onClick={handleMarkAllRead}
                      >
                        Marchează toate citite
                      </Button>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-muted-foreground">
                        Nu există notificări
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <DropdownMenuItem
                          key={notif.id}
                          className={`flex flex-col items-start gap-1 p-3 cursor-pointer ${
                            !notif.is_read ? "bg-secondary/50" : ""
                          }`}
                          onClick={() => {
                            handleMarkAsRead(notif.id);
                            // Navigate based on notification type
                            if (notif.type === "document_request" || notif.type === "document_access_approved" || notif.type === "document_access_rejected") {
                              navigate("/document-requests");
                            } else if (notif.type === "task_assigned" || notif.type === "task_completed" || notif.type === "task_reassigned") {
                              navigate("/tasks");
                            } else if (notif.type === "court_date_sync" || notif.type === "final_solution") {
                              navigate("/calendar");
                            } else if (notif.related_case_id) {
                              navigate("/cases");
                            } else {
                              navigate("/dashboard");
                            }
                          }}
                          data-testid={`notification-${notif.id}`}
                        >
                          <span className="font-body text-sm">{notif.message}</span>
                          <span className="font-mono text-xs text-muted-foreground">
                            {format(new Date(notif.created_at), "dd/MM/yyyy HH:mm")}
                          </span>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="user-menu-button">
                    <User className="w-5 h-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="user-dropdown">
                  <DropdownMenuLabel className="font-heading">{user?.name}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-destructive focus:text-destructive cursor-pointer"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Deconectare
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-6">
          <Outlet />
        </main>
      </div>

      {/* Scroll to top button */}
      {showScrollTop && (
        <Button
          onClick={scrollToTop}
          className="fixed bottom-20 right-6 z-50 rounded-full w-12 h-12 shadow-lg bg-primary hover:bg-primary/90"
          size="icon"
          data-testid="scroll-to-top"
        >
          <ArrowUp className="w-5 h-5" />
        </Button>
      )}
    </div>
  );
}
